import React, {useEffect, useRef, useState} from "react";
import {getById} from "../../services/service-order-evaluation.service";
import {NavigateFunction, useNavigate, useParams} from "react-router-dom";
import {Chip as ChipMaterial, InputAdornment, TextField, Tooltip as MaterialTooltip, FormControlLabel, Checkbox} from "@mui/material";
import {Box, Breadcrumbs, Button, Grid, Stack, Tooltip, Typography} from "@mui/joy";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import StatusHistoryModal from "../utils/StatusHistoryModal";
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import GradingIcon from "@mui/icons-material/Grading";
import IServiceOrderEvaluation from "../../types/service-order-evaluation.type";
import {ServiceOrderEvaluationStatus} from "../../types/enumeration/ServiceOrderEvaluationStatus";
import {ServiceOrderEvaluationStatusColors} from "../../types/enumeration/ServiceOrderEvaluationStatusColors";
import {ServiceOrderEvaluationStatusNames} from "../../types/enumeration/ServiceOrderEvaluationStatusNames";
import {WorkPositionNames} from "../../types/enumeration/WorkPositionNames";
import {WorkPosition} from "../../types/enumeration/WorkPosition";
import {RevisionItemCategory} from "../../types/enumeration/RevisionItemCategory";
import {ArrowBackIosNew} from "@mui/icons-material";
import IServiceOrderEvaluationItem from "../../types/service-order-evaluation-item.type";
import Divider from "@mui/joy/Divider";

export const MyEvaluationView: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const { id } = useParams();
  const [serviceOrderEvaluation, setServiceOrderEvaluation] = React.useState<IServiceOrderEvaluation>();
  const [statusHistoryModalOpen, setStatusHistoryModalOpen] = React.useState(false);

  const goBack = () => {
    navigate(-1)
  };

  const handleStatusHistoryModalOpen = () => {
    setStatusHistoryModalOpen(true);
  }

  const handleStatusHistoryModalClose = () => {
    setStatusHistoryModalOpen(false);
  }

  const TruncatedDescription: React.FC<{ text: string }> = ({ text }) => {
    const textRef = useRef<HTMLDivElement | null>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
      const element = textRef.current;
      if (!element) {
        return;
      }

      const updateOverflow = () => {
        setIsOverflowing(element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth);
      };

      updateOverflow();

      const resizeObserver = new ResizeObserver(updateOverflow);
      resizeObserver.observe(element);

      window.addEventListener("resize", updateOverflow);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", updateOverflow);
      };
    }, [text]);

    return (
      <Tooltip title={text} arrow placement="top-start" disableHoverListener={!isOverflowing}>
        <Box
          ref={textRef}
          sx={{
            minWidth: 0,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
            lineHeight: 1.35,
            maxWidth: "100%",
          }}
        >
          {text}
        </Box>
      </Tooltip>
    );
  };

  const renderEvaluationSection = (
    title: string,
    category: RevisionItemCategory,
    allItems: IServiceOrderEvaluationItem[]
  ) => {
    const filteredItems = allItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.revisionItem.category === category);

    return (
      <>
        <Divider sx={{ my: 2 }}>{title}</Divider>
        {filteredItems.map(({ item, index }) => {
          return (
            <Box
              key={item.id || index}
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 110px 120px 90px 110px 1.8fr",
                gap: 1,
                alignItems: "center",
                px: 1,
                py: 1,
              }}
            >
              <TruncatedDescription text={item.revisionItem.description} />
              <CssVarsProvider>
                <TextField
                  label="Clasificacion"
                  type="number"
                  variant="filled"
                  value={item.rating ?? ""}
                  disabled={Boolean(item.notApply)}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { readOnly: true, style: { textAlign: "center" }},
                  }}
                />
              </CssVarsProvider>
              <CssVarsProvider>
                <TextField
                  label="Ponderación"
                  value={item.weighing ?? 0}
                  disabled={Boolean(item.notApply)}
                  variant="filled"
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { readOnly: true, style: { textAlign: "right" } },
                    input: {
                      endAdornment: <InputAdornment sx={{mt: 2, mr: 3}} position="end">%</InputAdornment>,
                    }
                  }}
                />
              </CssVarsProvider>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CssVarsProvider>
                  <MaterialTooltip title={"No Aplica"} arrow placement="right-end">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(item.notApply)}
                          disabled
                        />
                      }
                      label="N/A"
                    />
                  </MaterialTooltip>
                </CssVarsProvider>
              </Box>

              <CssVarsProvider>
                <TextField
                  label="Resultado"
                  variant="filled"
                  value={item.result ?? ""}
                  disabled={Boolean(item.notApply)}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { readOnly: true, style: { textAlign: "center" } },
                  }}
                />
              </CssVarsProvider>

              <CssVarsProvider>
                <TextField
                  label="Observaciones"
                  variant="filled"
                  multiline
                  minRows={1}
                  value={item.notes ?? ""}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { readOnly: true },
                  }}
                />
              </CssVarsProvider>
            </Box>
          );
        })}
      </>
    );
  };

  const renderEvaluationFooter = () => (
    <Box
      sx={{
        mt: 2,
        px: 1,
        py: 1,
        display: "grid",
        gridTemplateColumns: "1fr 110px 120px 90px 110px 1.8fr",
        alignItems: "center",
        columnGap: 1,
        borderTop: "1px solid #d9d9d9",
        borderBottom: "1px solid #d9d9d9",
        backgroundColor: "#fafafa",
      }}
    >
      <Box sx={{ gridColumn: "1 / span 3" }} />

      <Box sx={{ gridColumn: 5, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <CssVarsProvider>
          <TextField
            label="Resultado Total"
            value={serviceOrderEvaluation?.result}
            variant="filled"
            sx={{ width: 110 }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { readOnly: true, style: { textAlign: "center" } },
            }}
          />
        </CssVarsProvider>
      </Box>

      <Box sx={{ gridColumn: 6, display: "flex", alignItems: "flex-start", justifyContent: "flex-end", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
          <CssVarsProvider>
            <TextField
              id="hours"
              label="Horas Acreditadas"
              name="hours"
              value={serviceOrderEvaluation?.hours}
              type="number"
              variant="filled"
              sx={{ width: 120 }}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { readOnly: true, style: { textAlign: "center" } },
              }}
            />
          </CssVarsProvider>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
          <CssVarsProvider>
            <TextField
              label="Bono Completado"
              value={serviceOrderEvaluation?.bonus}
              type="number"
              variant="filled"
              sx={{ width: 120 }}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { readOnly: true, style: { textAlign: "center" } },
              }}
            />
          </CssVarsProvider>
        </Box>
      </Box>
    </Box>
  );

  const fetchServiceOrderEvaluation = async () => {
    if (id) {
      getById(id).then( (response) => {
        setServiceOrderEvaluation(response.data)
      })
    }
  };

  useEffect(() => {
    fetchServiceOrderEvaluation();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 1 },
              width: '100%',
            }}
          >
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <GradingIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                Mis Evaluaciones
              </Typography>
              <Typography level="h4" component="h1">
                Consultar
              </Typography>
            </Breadcrumbs>
            <Box>
              <CssVarsProvider>
                <ChipMaterial
                  color={ServiceOrderEvaluationStatusColors[ServiceOrderEvaluationStatus[serviceOrderEvaluation?.status as keyof typeof ServiceOrderEvaluationStatus]]}
                  label={ServiceOrderEvaluationStatusNames[ServiceOrderEvaluationStatus[serviceOrderEvaluation?.status as keyof typeof ServiceOrderEvaluationStatus]]}
                  sx={{width: 200, fontWeight: 'bold', fontSize: 15}}
                  icon={<ManageSearchIcon fontSize="medium" sx={{marginTop: -0.5}} />}
                  clickable={true}
                  onClick={() => handleStatusHistoryModalOpen()}
                />
              </CssVarsProvider>
            </Box>
          </Box>

          <StatusHistoryModal
            open={statusHistoryModalOpen}
            title={"Historial de la Evaluacion de Desempeño"}
            history={serviceOrderEvaluation?.statusHistory}
            onCancel={handleStatusHistoryModalClose}
            historyType={"serviceOrderEvaluation"}
          />

          <Box sx={{ width: '100%', typography: 'body1' }}>
            <Grid
              container
              rowSpacing={2}
              columnSpacing={2}
              marginBlock={1}
              marginInline={0}
              sx={{ width: '100%' }}
            >
              <Grid xl={6} md={6} sm={6} xs={6}>
                <CssVarsProvider>
                  <TextField
                    fullWidth
                    id="notes"
                    label="Observaciones"
                    name="notes"
                    variant="filled"
                    type={"text"}
                    multiline
                    value={serviceOrderEvaluation?.notes}
                    rows={4}
                    slotProps={{
                      inputLabel: {shrink: true},
                      input: {readOnly: true}
                    }}
                  />
                </CssVarsProvider>
              </Grid>
              <Grid xl={6} md={6} sm={6} xs={6} container rowSpacing={1.5} >
                <Grid xl={6} md={6} sm={6} xs={6}>
                  <CssVarsProvider>
                    <TextField
                      fullWidth
                      id="serviceOrder"
                      label="Orden de Servicio"
                      name="serviceOrder"
                      variant="filled"
                      type={"text"}
                      value={serviceOrderEvaluation?.serviceOrder?.number}
                      slotProps={{
                        inputLabel: {shrink: true},
                        input: {readOnly: true}
                      }}
                    />
                  </CssVarsProvider>
                </Grid>
                <Grid xl={6} md={6} sm={6} xs={6}>
                  <CssVarsProvider>
                    <TextField
                      fullWidth
                      id="position"
                      label="Puesto"
                      name="position"
                      variant="filled"
                      type={"text"}
                      value={WorkPositionNames[WorkPosition[serviceOrderEvaluation?.workPosition as keyof typeof WorkPosition]]}
                      slotProps={{
                        inputLabel: {shrink: true},
                        input: {readOnly: true}
                      }}
                    />
                  </CssVarsProvider>
                </Grid>
                <Grid xl={12} md={12} sm={12} xs={12}>
                  <CssVarsProvider>
                    <TextField
                      fullWidth
                      id="number"
                      label="Bono"
                      name="number"
                      variant="filled"
                      type={"text"}
                      value={serviceOrderEvaluation?.bonusUpTo}
                      slotProps={{
                        inputLabel: {shrink: true},
                        input: {readOnly: true}
                      }}
                    />
                  </CssVarsProvider>
                </Grid>
              </Grid>
            </Grid>

            {renderEvaluationSection(
              "Objetivos Basicos",
              RevisionItemCategory.BASIC,
              serviceOrderEvaluation?.evaluationItems ? serviceOrderEvaluation?.evaluationItems : []
            )}
            {renderEvaluationSection(
              "Objetivos Especificos",
              RevisionItemCategory.SPECIFIC,
              serviceOrderEvaluation?.evaluationItems ? serviceOrderEvaluation?.evaluationItems : []
            )}
            {renderEvaluationFooter()}

            <Stack direction="row" justifyContent="flex-end" mt={1}>
              <Button onClick={() => goBack()} variant="outlined" endDecorator={<ArrowBackIosNew />}>Volver</Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};


